import {
  Component,
  ElementRef,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import {
  BookingPermission,
  CouponPermission,
  CustomFieldPermission,
  InvoicePermission,
  RoomPermission,
  UserPermission,
  VendorPermission,
} from '../../core/models/entities/permission';
import { Store } from '@ngrx/store';
import { fromStore } from 'angular/app/core/store';
import { ModalService } from '../../core/services/modal.service';
import { AuthService } from '../../core/services/auth.service';
import { ToastService } from '../../core/services/toast.service';
import { map } from 'rxjs/operators';
import { LogoutBaseComponent } from '../../shared/components/logout-base/logout-base.component';
import { ChangelogService } from 'angular/app/changelog/services/changelog.service';
import { ChangelogModalComponent } from 'angular/app/shared/components/changelog-modal/changelog-modal.component';
import { APP_VERSION } from '../../core/constants';
import {
  selectAuthVendor,
  selectInvoicingOpenCount,
} from '../../core/store/reducers';
import { PaymentProvider } from '../../core/models/entities/vendor';
import { gsap } from 'gsap';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter } from 'rxjs';

@Component({
  selector: 'app-sidebar-links',
  template: `
    <div class="flex min-h-full flex-col justify-between space-y-4 pt-4">
      <div class="space-y-px">
        <div id="sidebar-users" class="mr-6 pl-8 lg:hidden">
          @if (currentUser()?.vendors?.length > 1) {
            <app-vendor-selection
            ></app-vendor-selection>
          }
          @if (currentUser()?.vendors?.length === 1) {
            <div
              class="py-4 text-sm uppercase"
              >
              {{ currentUser()?.vendor?.name }}
            </div>
          }
        </div>
    
        <div id="sidebar-bookings">
          <a
            routerLink="/reservations"
            routerLinkActive="bg-gray-100 font-medium border-r-[3px] border-secondary-500"
            class="text-secondary-500 flex w-full items-center space-x-1.5 py-2 pl-8 no-underline hover:bg-gray-100"
            >
            <div class="w-6 flex-none text-left">
              <i class="fal fa-calendar-day relative">
                <ng-template [appHasPermission]="BookingPermission.Update">
                  @if (pendingBookings$ | async) {
                    <div
                      class="absolute -top-0.5 -right-1 h-2.5 w-2.5 rounded-full bg-pink-500"
                    ></div>
                  }
                </ng-template>
              </i>
            </div>
            <span>Réservations</span>
          </a>
        </div>
    
        <ng-template [appHasPermission]="RoomPermission.Store">
          <div id="sidebar-rooms">
            <a
              routerLink="/salles"
              routerLinkActive="bg-gray-100 font-medium border-r-[3px] border-secondary-500"
              class="text-secondary-500 flex w-full items-center space-x-1.5 py-2 pl-8 no-underline hover:bg-gray-100"
              >
              <div class="w-6 flex-none text-left">
                <i class="fal fa-house"></i>
              </div>
              <span>Salles</span>
            </a>
          </div>
        </ng-template>
    
        <ng-template [appHasPermission]="CouponPermission.ViewAny">
          <div id="sidebar-coupons">
            <a
              routerLink="/codes-promo"
              routerLinkActive="bg-gray-100 font-medium border-r-[3px] border-secondary-500"
              class="text-secondary-500 flex w-full items-center space-x-1.5 py-2 pl-8 no-underline hover:bg-gray-100"
              >
              <div class="w-6 flex-none text-left">
                <i class="fal fa-percentage"></i>
              </div>
              <span>Codes promo</span>
            </a>
          </div>
        </ng-template>
    
        <ng-template [appHasPermission]="InvoicePermission.ViewAny">
          <app-sidebar-subtitle>Comptabilité</app-sidebar-subtitle>
    
          <div id="sidebar-invoicing">
            <a
              routerLink="/facturation"
              routerLinkActive="bg-gray-100 items-center font-medium border-r-[3px] border-secondary-500"
              class="text-secondary-500 flex w-full items-center space-x-1.5 py-2 pl-8 no-underline hover:bg-gray-100"
              >
              <div class="w-6 flex-none text-left">
                <i class="fal fa-cash-register relative">
                  <ng-template [appHasPermission]="BookingPermission.Update">
                    @if (openInvoicesCount()) {
                      <div
                        class="absolute -top-0.5 -right-1 h-2.5 w-2.5 rounded-full bg-pink-500"
                      ></div>
                    }
                  </ng-template>
                </i>
              </div>
              <span>Facturation</span>
            </a>
          </div>
    
          @if (vendor()?.accepts_online_payments) {
            <div id="sidebar-invoicing">
              <a
                [href]="
                  vendor()?.payment_provider === PaymentProvider.Wallee
                    ? 'https://app-wallee.com/s/' +
                      vendor()?.wallee_space_id +
                      '/space'
                    : 'https://' +
                      (vendor().test_mode || vendor().is_ercos
                        ? 'test'
                        : vendor().payrexx_instance_name) +
                      '.pay.location-salles.ch/cadmin/index.php'
                "
                target="_blank"
                class="text-secondary-500 flex w-full items-center space-x-1.5 py-2 pl-8 no-underline hover:bg-gray-100"
                >
                <div class="w-6 flex-none text-left">
                  <i class="fal fa-arrow-up-right-from-square"></i>
                </div>
                <span>{{ vendor().payment_provider | titleCase }}</span>
              </a>
            </div>
          }
        </ng-template>
    
        <ng-template [appHasPermission]="UserPermission.ViewAny">
          <app-sidebar-subtitle>Paramètres</app-sidebar-subtitle>
    
          <div id="sidebar-users">
            <a
              routerLink="/utilisateurs"
              routerLinkActive="bg-gray-100 font-medium border-r-[3px] border-secondary-500"
              class="text-secondary-500 flex w-full items-center space-x-1.5 py-2 pl-8 no-underline hover:bg-gray-100"
              >
              <div class="w-6 flex-none text-left">
                <i class="fal fa-user-group"></i>
              </div>
              <span>Utilisateurs</span>
            </a>
          </div>
    
          <ng-template [appHasPermission]="VendorPermission.Update">
            <div id="sidebar-settings">
              <a
                class="text-secondary-500 flex w-full items-center space-x-1.5 py-2 pl-8 no-underline hover:bg-gray-100"
                (click)="toggleSettings()"
                >
                <div class="w-6 flex-none">
                  <i class="fal fa-cog"></i>
                </div>
                <span>Réglages</span>
                <i
                  class="fal fa-chevron-down -translate-y-px transform transition-all"
                  [ngClass]="{
                    'rotate-180': !settingsCollapsed(),
                  }"
                ></i>
              </a>
            </div>
    
            <div class="h-0 overflow-hidden text-sm" #collapsibleContent>
              <a
                class="text-secondary-500 block w-full py-2 pl-16 no-underline hover:bg-gray-100"
                routerLink="/reglages/groupes-de-tarifs"
                routerLinkActive="bg-gray-100 font-medium border-r-[3px] border-secondary-500"
                >
                Groupes de tarifs
              </a>
    
              <a
                class="text-secondary-500 block w-full py-2 pl-16 no-underline hover:bg-gray-100"
                routerLink="/reglages/conditions-annulation"
                routerLinkActive="bg-gray-100 font-medium border-r-[3px] border-secondary-500"
                >
                Conditions d'annulation
              </a>
    
              <ng-template [appHasPermission]="CustomFieldPermission.ViewAny">
                <ng-template appFeature="custom_fields" let-isBeta="isBeta">
                  <a
                    class="text-secondary-500 block w-full py-2 pl-16 no-underline hover:bg-gray-100"
                    routerLink="/reglages/champs-personnalises"
                    routerLinkActive="bg-gray-100 font-medium border-r-[3px] border-secondary-500"
                    >
                    Champs personnalisés
                    @if (isBeta) {
                      <span class="-translate-y-1 px-1">
                        <app-badge variant="pink">beta</app-badge>
                      </span>
                    }
                  </a>
                </ng-template>
              </ng-template>
    
              <a
                class="text-secondary-500 block w-full py-2 pl-16 no-underline hover:bg-gray-100"
                routerLink="/reglages/ouverture-calendrier"
                routerLinkActive="bg-gray-100 font-medium border-r-[3px] border-secondary-500"
                >
                Ouverture calendrier
              </a>
    
              <a
                class="text-secondary-500 block w-full py-2 pl-16 no-underline hover:bg-gray-100"
                routerLink="/reglages/modeles-communication"
                routerLinkActive="bg-gray-100 font-medium border-r-[3px] border-secondary-500"
                >
                Modèles communication
              </a>
    
              <a
                class="text-secondary-500 block w-full py-2 pl-16 no-underline hover:bg-gray-100"
                routerLink="/reglages/modes-de-paiement"
                routerLinkActive="bg-gray-100 font-medium border-r-[3px] border-secondary-500"
                >
                Modes de paiement
              </a>
    
              <a
                class="text-secondary-500 block w-full py-2 pl-16 no-underline hover:bg-gray-100"
                routerLink="/reglages/securite"
                routerLinkActive="bg-gray-100 font-medium border-r-[3px] border-secondary-500"
                >
                Sécurité
              </a>
    
              <a
                class="text-secondary-500 block w-full py-2 pl-16 no-underline hover:bg-gray-100"
                routerLink="/reglages/mode-test"
                routerLinkActive="bg-gray-100 font-medium border-r-[3px] border-secondary-500"
                >
                Mode test
              </a>
            </div>
    
            <div id="sidebar-account">
              <a
                routerLink="/administration"
                routerLinkActive="bg-gray-100 font-medium border-r-[3px] border-secondary-500"
                class="text-secondary-500 flex w-full items-center space-x-1.5 py-2 pl-8 no-underline hover:bg-gray-100"
                >
                <div class="w-6 flex-none text-left">
                  <i class="fal fa-building"></i>
                </div>
                <span>Organisation</span>
              </a>
            </div>
          </ng-template>
        </ng-template>
      </div>
    
      <!-- -------------------------------------------------------------------------------------------------------->
      <!-- Bas de la sidebar -->
      <!-- -------------------------------------------------------------------------------------------------------->
    
      <div id="sidebar-profile-page" class="pb-6 pl-8">
        @if (currentUser()) {
          <div class="mt-6 mr-12 mb-6">
            <a
              class="hover:border-secondary-500 flex w-full items-center justify-center rounded-sm border bg-white py-2 no-underline"
              href="/louer-salle/{{ currentUser().vendor.slug }}"
              target="_blank"
              >Page publique
              <i class="fal fa-external-link ml-2"></i>
            </a>
          </div>
        }
    
        <a class="flex w-full py-1 no-underline">
          <div class="w-full">
            <div class="text-left text-lg font-medium">{{
              currentUser()?.first_name
            }}</div>
            <div class="mt-2 flex gap-3 text-sm">
              <button
                class="hover:border-secondary-500 flex h-10 w-10 items-center justify-center rounded-sm border bg-white"
                [routerLink]="['/compte']"
                routerLinkActive="border-secondary-500"
                data-cy="user-profile"
                ><i class="fal fa-user-pen"></i
              ></button>
              <button
                class="hover:border-secondary-500 flex h-10 w-10 items-center justify-center rounded-sm border bg-white"
                (click)="logout()"
                ><i class="fal fa-power-off"></i
              ></button>
            </div>
          </div>
        </a>
        <div class="mt-2 text-left">
          <button class="relative text-sm" (click)="openChangelogModal()"
            >Notes de version {{ version
            }}@if (changelogService.hasUnreadChanges$ | async) {
            <div
              class="absolute -top-0.5 -right-1 h-2.5 w-2.5 rounded-full bg-pink-500"
              ></div
              >
            }</button>
          </div>
        </div>
      </div>
    `,
  styles: [],
  standalone: false,
})
export class SidebarLinksComponent extends LogoutBaseComponent {
  RoomPermission = RoomPermission;
  BookingPermission = BookingPermission;
  UserPermission = UserPermission;
  VendorPermission = VendorPermission;
  InvoicePermission = InvoicePermission;
  CouponPermission = CouponPermission;
  currentUser = this.store.selectSignal(fromStore.selectAuthUser);
  pendingBookings$ = this.store
    .select(fromStore.selectAllPendingBookingsCount)
    .pipe(map((pendingBookings) => pendingBookings > 0));
  openInvoicesCount = this.store.selectSignal(selectInvoicingOpenCount);
  vendor = this.store.selectSignal(selectAuthVendor);
  settingsCollapsed = signal(true);
  collapsibleContent = viewChild<ElementRef>('collapsibleContent');
  protected readonly PaymentProvider = PaymentProvider;
  version = APP_VERSION;
  router = inject(Router);
  route = inject(ActivatedRoute);

  constructor(
    private store: Store,
    public changelogService: ChangelogService,
    modalService: ModalService,
    authService: AuthService,
    toastService: ToastService,
  ) {
    super(modalService, authService, toastService);
    this.router.events
      .pipe(
        takeUntilDestroyed(),
        filter((event) => event instanceof NavigationEnd),
      )
      .subscribe(() => {
        this.toggleSettingsWhenNecessary();
      });
  }

  ngAfterViewInit() {
    this.toggleSettingsWhenNecessary();
  }

  openChangelogModal() {
    this.modalService.open(ChangelogModalComponent, {
      title: 'Notes de version',
      showActionButtons: false,
      size: 'lg',
    });
  }

  /**
   * Ferme le collapse des réglages si on quitte les réglages
   * Ouvre le collapse des réglages si on est arrivé sur la page par un autre lien (on n'a pas ouvert manuellement le menu)
   */
  toggleSettingsWhenNecessary() {
    let currentRoute = this.route;
    while (currentRoute.firstChild) {
      currentRoute = currentRoute.firstChild;
    }

    const isChildRouteSettings =
      !!currentRoute.parent && this.router.url.startsWith('/reglages');

    if (isChildRouteSettings && this.settingsCollapsed()) {
      this.toggleSettings(); // on ouvre les réglages si ce n'est pas le cas
    } else if (!isChildRouteSettings && !this.settingsCollapsed()) {
      this.toggleSettings(); // on a quitté les réglages donc on ferme le collapse
    }
  }

  toggleSettings() {
    this.settingsCollapsed.set(!this.settingsCollapsed());
    const content = this.collapsibleContent().nativeElement;

    if (this.settingsCollapsed()) {
      gsap.to(content, { height: 0, duration: 0.3, ease: 'power2.inOut' });
    } else {
      gsap.to(content, { height: 'auto', duration: 0.3, ease: 'power2.inOut' });
    }
  }

  protected readonly CustomFieldPermission = CustomFieldPermission;
}
